import { Box, Flex } from "@chakra-ui/react";
import PopoverTagColumn from "components/Column/PopoverTagDisplay";
import { PlusNTag } from "components/Tag/PlusNTag";

interface DisplayResourceTargetsColumnProps<T> {
  items: T[];
  renderTag: (item: T) => React.ReactNode;
  renderAvatar: (item: T) => React.ReactNode;
  getKey: (item: T) => string;
  emptyLabel: string;
  maxAvatarCount?: number;
}

interface AvatarWrapperProps {
  children: React.ReactNode;
}

function AvatarWrapper({ children }: AvatarWrapperProps) {
  return (
    <Box ml="-6px">
      {children}
    </Box>
  );
}

/**
 * Generic component for displaying resource targets (recipients, assignees, attendees) in a column
 * with avatar previews and a popover for full list.
 *
 * Usage:
 * <DisplayResourceTargetsColumn
 *   items={recipients}
 *   renderTag={(recipient) => <RecipientTag recipient={recipient} disabled />}
 *   renderAvatar={(recipient) => <RecipientAvatar recipient={recipient} />}
 *   getKey={(recipient) => recipient.id || recipient.customEmail}
 *   emptyLabel="No Recipients"
 * />
 */
export default function DisplayResourceTargetsColumn<T>({
  items,
  renderTag,
  renderAvatar,
  getKey,
  emptyLabel,
  maxAvatarCount = 3,
}: DisplayResourceTargetsColumnProps<T>) {
  return (
    <PopoverTagColumn<T>
      items={items}
      renderTag={renderTag}

      displaySummary={(displayItems: T[]) => (
        <Flex align="center" mx="2" flexDirection="row-reverse">
          <PlusNTag
            maxCount={maxAvatarCount}
            items={displayItems}
            ml="-6px"
            size="33px"
          />
          {displayItems.slice(0, maxAvatarCount).reverse().map((item) => (
            <AvatarWrapper key={getKey(item)}>
              {renderAvatar(item)}
            </AvatarWrapper>
          ))}
        </Flex>
      )}
      emptyLabel={emptyLabel}
      popoverContainerContentProps={{ marginTop: "1" }}
    />
  );
}
